<!--
  @component UserLogin
  Full-screen centered welcome + sign-in form (no logo column).
-->
<template>
  <AuthWelcomeShell
    title="Welcome back"
    lead="Good to see you again. Sign in and pick up where you left off—we've saved your place."
  >
    <div
      v-if="successMessage"
      class="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
    >
      {{ successMessage }}
    </div>

    <div
      v-if="pendingVerificationHint"
      class="mb-4 rounded-lg border border-amber-200 bg-amber-50/90 p-3 text-sm text-amber-950"
    >
      {{ pendingVerificationHint }}
    </div>

    <div class="mb-4">
      <label
        for="username"
        class="mb-2 block text-sm font-medium text-stone-800"
      >Email <span class="text-red-500">*</span></label>
      <IconField>
        <InputIcon class="pi pi-envelope" />
        <InputText
          id="username"
          v-model="email"
          type="email"
          placeholder="you@company.com"
          class="w-full"
          autocomplete="email"
        />
      </IconField>
    </div>

    <div class="mb-4">
      <label
        for="password"
        class="mb-2 block text-sm font-medium text-stone-800"
      >Password <span class="text-red-500">*</span></label>
      <IconField>
        <InputIcon class="pi pi-lock" />
        <InputText
          id="password"
          v-model="password"
          type="password"
          placeholder="Your password"
          class="w-full"
          autocomplete="current-password"
        />
      </IconField>
    </div>

    <div class="mb-4 flex items-center justify-between">
      <div class="flex items-center">
        <Checkbox
          id="rememberme1"
          v-model="check"
          :binary="true"
          class="mr-2"
        />
        <label for="rememberme1">Remember me</label>
      </div>
      <a class="cursor-pointer text-sm font-medium text-amber-800 no-underline hover:text-amber-950">Forgot password?</a>
    </div>

    <div
      v-if="errorMessage"
      class="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
    >
      {{ errorMessage }}
    </div>

    <div
      v-if="showResendVerification"
      class="mb-4"
    >
      <Button
        label="Resend verification email"
        icon="pi pi-envelope"
        severity="secondary"
        outlined
        size="small"
        class="w-full"
        :loading="resendLoading"
        @click="resendVerification"
      />
    </div>

    <Button
      label="Sign in"
      icon="pi pi-arrow-right"
      icon-pos="right"
      class="w-full"
      :loading="loading"
      @click="loginUser"
    />

    <template #after>
      <span>New organisation?</span>
      <RouterLink
        to="/register"
        class="ml-1 font-semibold text-amber-900 underline decoration-amber-300 underline-offset-2 hover:text-amber-950"
      >
        Create an account
      </RouterLink>
    </template>
  </AuthWelcomeShell>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { getDashboardByRole, getDashboardByUsername } from '@/utils/userRoles.js';
import { useAuthStore } from '@/stores/authStore';
import AuthWelcomeShell from '@/components/auth/AuthWelcomeShell.vue';
import { resendVerificationEmail } from '@/services/AuthService';

const email = ref('');
const password = ref('');
const errorMessage = ref('');
const loading = ref(false);
const check = ref(false);
const successMessage = ref('');
const pendingVerificationHint = ref('');
const showResendVerification = ref(false);
const resendLoading = ref(false);

const router = useRouter();
const route = useRoute();

onMounted(() => {
  if (route.query.verified === '1') {
    successMessage.value =
      'Email verified. You can sign in below.';
  }
  if (route.query.pending_verification === '1') {
    pendingVerificationHint.value =
      'Verify your email using the link we sent, then sign in to continue (e.g. finish inviting your team).';
  }
  const qEmail = route.query.email;
  if (typeof qEmail === 'string' && qEmail.trim()) {
    email.value = qEmail.trim();
  }
});

async function resendVerification() {
  errorMessage.value = '';
  showResendVerification.value = false;
  resendLoading.value = true;
  try {
    await resendVerificationEmail(email.value);
    successMessage.value =
      'If this email has an unverified account, we sent a new verification link.';
  } catch {
    errorMessage.value = 'Could not send the email. Try again shortly.';
  } finally {
    resendLoading.value = false;
  }
}

const loginUser = async () => {
  errorMessage.value = '';
  showResendVerification.value = false;
  loading.value = true;
  try {
    const authStore = useAuthStore();
    const response = await authStore.login(email.value, password.value);

    const username = response.user.username || email.value.split('@')[0];
    if (!response.reset_required) {
      const dashboardRoute =
        getDashboardByRole(response.user) ?? getDashboardByUsername(username);
      router.push(dashboardRoute);
    }
  } catch (error) {
    if (error.response?.status === 419) {
      errorMessage.value =
        'Session expired. Please refresh the page and try again.';
    } else if (error.response?.status === 403 && error.response?.data?.code === 'email_not_verified') {
      errorMessage.value =
        error.response?.data?.message ||
        'Please verify your email before signing in.';
      showResendVerification.value = true;
    } else if (error.response?.status === 422) {
      errorMessage.value =
        error.response.data.message ||
        'Those details do not match our records. Try again?';
    } else if (error.response?.status === 429) {
      errorMessage.value = 'Too many attempts—take a short break and try again.';
    } else if (error.response?.status >= 500) {
      errorMessage.value = 'Something went wrong on our side. Please try again soon.';
    } else {
      errorMessage.value =
        error.response?.data?.message ||
        'Could not sign you in. Check your email and password.';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
label {
  letter-spacing: 0.01em;
}
</style>
